import { CustomBottomBar, CustomPageTitle } from '@components';
import { Button, Container, Dropdown, Input, Modal, Typography } from '@design-system';
import React from 'react';
import { FaUmbrellaBeach } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import './UpdateCongePage.scss';
import { UpdateCongeSkeleton } from './UpdateCongeSkeleton';
import { useUpdateCongePage } from './useUpdateCongePage';

export const UpdateCongePage: React.FC = () => {
    const {
        holiday,
        holidayTypes,
        operators,
        handleUpdateHoliday,
        isLoading,
        error,
        updateHolidayToUpdate,
        isHolidayUpdated,
        openDeleteModal,
        setOpenDeleteModal,
        confirmDeleteHoliday,
        isRefused,
        isCancelled
    } = useUpdateCongePage();

    if (error) {
        return <div>Une erreur est survenue</div>;
    }

    return (
        <div className="update-conge-page">
            {isLoading ? (
                <UpdateCongeSkeleton />
            ) : (
                <>
                    {holiday && holidayTypes && operators && (
                        <>
                            <CustomPageTitle>
                                <Typography intrinsic="h3" iconColor="yellow" icon={<FaUmbrellaBeach />} iconFilled iconSize={20}>
                                    Demande #{holiday.holiday_id}
                                </Typography>
                            </CustomPageTitle>
                            <Container>
                                <Container bordered="grey" className="update-holiday-form">
                                    <div className="update-holiday-form-header">
                                        <Typography intrinsic="h3">Demande en cours</Typography>
                                        <Button
                                            color="grey"
                                            variant="tertiary"
                                            icon={<IoMdClose />}
                                            iconPosition="right"
                                            iconSize={20}
                                            disabled={isRefused || isCancelled}
                                            onClick={() => {
                                                setOpenDeleteModal(true);
                                            }}
                                        >
                                            <Typography intrinsic="h4">Supprimer</Typography>
                                        </Button>
                                    </div>
                                    <Dropdown
                                        options={holidayTypes.map((holidayType) => ({
                                            id: holidayType.id.toString(),
                                            label: holidayType.label
                                        }))}
                                        placeholder="Type d'absence"
                                        value={holidayTypes
                                            .map((holidayType) => ({
                                                id: holidayType.id.toString(),
                                                label: holidayType.label
                                            }))
                                            .find((holidayType) => `${holidayType.id}` === `${holiday.type_id}`)}
                                        onOptionChange={(value) => updateHolidayToUpdate('type_id', value.id)}
                                        disabled={isRefused || isCancelled}
                                    />
                                    <Dropdown
                                        options={operators.map((operator) => ({
                                            id: operator.id_operator,
                                            label: operator.name
                                        }))}
                                        placeholder="Responsable"
                                        value={operators
                                            .map((operator) => ({
                                                id: operator.id_operator,
                                                label: operator.name
                                            }))
                                            .find((operator) => `${operator.id}` === `${holiday.operator_id}`)}
                                        onOptionChange={(value) => updateHolidayToUpdate('operator_id', value.id)}
                                        disabled={isRefused || isCancelled}
                                    />
                                    <Input
                                        type="date"
                                        label="Date de début"
                                        placeholder="Date de début"
                                        value={holiday.date_start.split(' ')[0]}
                                        onValueChange={(value: string) => updateHolidayToUpdate('date_start', value)}
                                        min={new Date().toISOString().split('T')[0]}
                                        disabled={isRefused || isCancelled}
                                    />
                                    <Input
                                        type="date"
                                        label="Date de fin"
                                        placeholder="Date de fin"
                                        value={holiday.date_end.split(' ')[0]}
                                        onValueChange={(value: string) => updateHolidayToUpdate('date_end', value)}
                                        min={holiday.date_start}
                                        disabled={isRefused || isCancelled}
                                    />
                                </Container>
                                {isCancelled && (
                                    <Container bordered="grey" className="update-holiday-reason mt-medium-spacing">
                                        <Typography intrinsic="h4">Demande annulée</Typography>
                                        <Typography intrinsic="p">
                                            Cette demande d'absence a été annulée et ne peut plus être modifiée.
                                        </Typography>
                                    </Container>
                                )}
                                {holiday.reason && !isCancelled && (
                                    <Container bordered="grey" className="update-holiday-reason mt-medium-spacing">
                                        <Typography intrinsic="h4">Motif du refus</Typography>
                                        <Typography intrinsic="p">{holiday.reason}</Typography>
                                    </Container>
                                )}
                            </Container>

                            <CustomBottomBar>
                                <Button
                                    onClick={() => handleUpdateHoliday(holiday)}
                                    disabled={!isHolidayUpdated || isRefused || isCancelled}
                                    color="marine"
                                    size="fullwidth"
                                >
                                    <Typography intrinsic="h4">Mettre à jour la demande</Typography>
                                </Button>
                            </CustomBottomBar>
                        </>
                    )}
                    {!isLoading && (!holiday || !holidayTypes || !operators) && (
                        <>
                            <CustomPageTitle>
                                <Typography intrinsic="h3" iconColor="yellow" icon={<FaUmbrellaBeach />} iconFilled iconSize={20}>
                                    Demande non trouvée
                                </Typography>
                            </CustomPageTitle>
                        </>
                    )}
                </>
            )}
            <Modal
                open={openDeleteModal}
                setClose={() => setOpenDeleteModal(false)}
                title="Confirmation"
                footer={
                    <div className="modal-footer">
                        <Button
                            color="grey"
                            variant="tertiary"
                            icon={<IoMdClose />}
                            iconPosition="right"
                            iconSize={20}
                            onClick={() => {
                                confirmDeleteHoliday();
                            }}
                        >
                            <Typography intrinsic="h4">Supprimer</Typography>
                        </Button>
                        <Button
                            color="red"
                            variant="secondary"
                            size="small"
                            onClick={() => {
                                setOpenDeleteModal(false);
                            }}
                        >
                            <Typography intrinsic="h4">Annuler</Typography>
                        </Button>
                    </div>
                }
            >
                <Typography intrinsic="p" variant="secondary">
                    Êtes-vous sûr de vouloir supprimer cette demande ?
                </Typography>
            </Modal>
        </div>
    );
};
